import { useMemo, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { useFlags } from '@mint-lib/routing-context';

import {
  Chip,
  Dialog,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@myn/mui';

import { Label, UserType } from '../../api/users/index.js';

type Props = {
  className?: string;
  onClose: () => void;
  userProfile: Partial<UserType>;
  isActive: boolean;
  tabs: { id: string; name: string }[];
};

const LabelModal: React.FC<Props> = ({
  onClose,
  isActive,
  userProfile,
  tabs,
}) => {
  const { t } = useTranslation('@myn/permissions');
  const ff = useFlags('@mint/arm', ['brandsAndGeoToMdm']);

  const [categoryType, setCategoryType] = useState<string>(
    ff.brandsAndGeoToMdm.enabled
      ? (userProfile.tags?.[0]?.categoryType ?? 'brands')
      : 'brands',
  );

  const filterLabels = useMemo(
    () =>
      userProfile.tags
        ?.filter((tag: Label) => tag.categoryType === categoryType)
        .sort((a: Label, b: Label) => +a.level - +b.level),
    [userProfile.tags, categoryType],
  );
  const subCategories = useMemo(
    () =>
      [...new Set(filterLabels?.map((tag: Label) => tag.category))].map(
        (subCategory: string | undefined) => {
          return {
            name: subCategory ?? '',
            labels: filterLabels?.filter(
              (label: Label) => label.category === subCategory,
            ),
          };
        },
      ),
    [filterLabels],
  );

  return (
    <Dialog
      open={isActive}
      onClose={onClose}
      title={
        `${userProfile.firstName} ${userProfile.lastName} ` +
        t('Assigned Labels')
      }
      size="m"
      submitButtonProps={{
        label: t('Close'),
        onClick: onClose,
        color: 'primary',
      }}
    >
      <div className="flex flex-col gap-4">
        {ff.brandsAndGeoToMdm.enabled ? (
          <Tabs
            value={categoryType}
            onChange={(_, newValue) => {
              setCategoryType(newValue);
            }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((tab) => (
              <Tab
                label={tab.name}
                value={ff.brandsAndGeoToMdm.enabled ? tab.name : tab.id}
                key={tab.id}
              />
            ))}
          </Tabs>
        ) : (
          <ToggleButtonGroup value={categoryType} size="large">
            <ToggleButton
              size="large"
              value="brands"
              label={t('Brands & products')}
              onClick={() => {
                setCategoryType('brands');
              }}
            />

            <ToggleButton
              size="large"
              value="geographies"
              label={t('Geographies')}
              onClick={() => {
                setCategoryType('geographies');
              }}
            />
          </ToggleButtonGroup>
        )}

        {subCategories.length === 0 ? (
          <div className="flex justify-center py-4">
            <Typography variant="h4">
              {t(`There are no labels assigned yet`)}
            </Typography>
          </div>
        ) : (
          subCategories.map((subCategory) => {
            return (
              <div className="flex flex-col gap-2" key={subCategory.name}>
                <Typography variant="h6">{subCategory.name}</Typography>
                <div className="flex flex-row flex-wrap gap-2">
                  {subCategory.labels?.map((label) => {
                    return (
                      <Chip
                        key={label.name}
                        label={label.name}
                        variant="outlined"
                        color="secondary"
                      />
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </Dialog>
  );
};

export default LabelModal;
